import { Fragment, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useReducedMotion, useTransform, type MotionValue } from 'framer-motion';
import { MousePointer2 } from 'lucide-react';

/** One run of the title. `select` = boxed like a design-tool selection,
 *  `marker` = highlighter stroke behind, `italic` = heavy italic accent. */
type TitlePart = { text: string; style?: "select" | "marker" | "italic" };

/** A tilted label stuck onto the title, positioned relative to the title box. */
type Sticker = {
  label: string;
  tone: "gold" | "navy" | "dot" | "bubble";
  className: string;
  rotate: number;
  cursor?: boolean;
};

type Slide = {
  eyebrow: string;
  title: TitlePart[];
  body?: string;
  bodyExtra?: string;
  stickers: Sticker[];
  features?: { icon: React.ReactNode; title: string; text: string }[];
};

const icon = (path: React.ReactNode) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);

const slides: Slide[] = [
  {
    eyebrow: "Who We Are:",
    title: [{ text: "Your" }, { text: "Trusted", style: "italic" }, { text: "Cold Chain", style: "select" }, { text: "Partner." }],
    body: "Providing premium refrigeration solutions in the Philippines for over 27 years.",
    stickers: [
      { label: "27+ years", tone: "gold", className: "-top-8 right-[6%]", rotate: -6 },
      { label: "Freezers & Chillers", tone: "navy", className: "top-[40%] -left-[12%]", rotate: -8 },
      { label: "Nationwide delivery", tone: "dot", className: "-bottom-6 -right-[6%]", rotate: 4, cursor: true },
    ],
  },
  {
    eyebrow: "Where It Started:",
    title: [{ text: "Our" }, { text: "Story.", style: "marker" }],
    body: "Founded in 1987, RK & K AG Prod, Inc. began in agriculture and refrigeration equipment. By 1996, we shifted our focus with the launch of RK & K Import Export, Inc., specializing in refrigeration solutions.",
    bodyExtra: "Now led by a new generation, we combine decades of experience with fresh ideas and a forward-thinking approach — driving us to improve and adapt to our clients' evolving needs.",
    stickers: [
      { label: "Est. 1987", tone: "gold", className: "-top-8 -left-[16%]", rotate: -8 },
      { label: "Import Export since 1996", tone: "bubble", className: "-top-12 -right-[34%]", rotate: 5 },
      { label: "A new generation", tone: "dot", className: "-bottom-7 -right-[14%]", rotate: -3, cursor: true },
    ],
  },
  {
    eyebrow: "Why We're Here:",
    title: [{ text: "Our" }, { text: "Mission.", style: "select" }],
    body: "Our mission is to deliver high-quality service and supply highly durable freezers and chillers across the Philippines. We are committed to ensuring exceptional customer care and efficient transactions, while fostering a healthy work environment that promotes work-life balance for our employees.",
    bodyExtra: "We strive to remain accessible and responsive to the needs of our clients, partners, and community.",
    stickers: [
      { label: "Built to last", tone: "gold", className: "-top-9 -right-[8%]", rotate: 6 },
      { label: "Customer care first", tone: "navy", className: "top-[45%] -left-[30%]", rotate: -6 },
      { label: "Work-life balance", tone: "dot", className: "-bottom-8 -right-[22%]", rotate: 3, cursor: true },
    ],
  },
  {
    eyebrow: "Where We're Going:",
    title: [{ text: "Our" }, { text: "Vision.", style: "marker" }],
    body: "RK & K envisions becoming the Philippines' most trusted provider of long-lasting, high-performance freezers and chillers. We strive to set the standard in product durability, accessibility of service, and customer satisfaction.",
    bodyExtra: "By consistently delivering excellence, we aim to not only meet but surpass expectations — turning every client interaction into a lasting partnership.",
    stickers: [
      { label: "Most trusted in PH", tone: "bubble", className: "-top-14 -left-[14%]", rotate: -4 },
      { label: "Surpass expectations", tone: "gold", className: "top-[38%] -right-[36%]", rotate: 7 },
      { label: "Lasting partnerships", tone: "dot", className: "-bottom-8 left-[8%]", rotate: -3, cursor: true },
    ],
  },
  {
    eyebrow: "What We Offer:",
    title: [{ text: "Built", style: "italic" }, { text: "Around" }, { text: "You.", style: "select" }],
    stickers: [
      { label: "Liebherr · Gramm", tone: "navy", className: "-top-9 right-[4%]", rotate: 5 },
      { label: "After-sales care", tone: "gold", className: "-bottom-5 -left-[10%]", rotate: -6, cursor: true },
    ],
    features: [
      {
        icon: icon(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />),
        title: "Durable Products",
        text: "Top-tier products from Liebherr, Gramm, and other global brands.",
      },
      {
        icon: icon(<><path d="M12 2l5 5-5 5-5-5 5-5z" /><path d="M12 12l5 5-5 5-5-5 5-5z" /></>),
        title: "After-Sales Care",
        text: "From consultation to check-ups, compressor replacements, and repairs.",
      },
      {
        icon: icon(<path d="M12 2s-1 1-1 3v7l-4 4v5h10v-5l-4-4V5s0-2-1-3z" />),
        title: "Hassle-Free",
        text: "Ensuring fast, efficient, and long-term support for all our clients.",
      },
      {
        icon: icon(<><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M20 8v6M23 11h-6" /></>),
        title: "New Leadership",
        text: "Combining decades of experience with fresh, forward-thinking ideas.",
      },
    ],
  },
];

const EASE_OUT = [0.16, 1, 0.3, 1] as const;
const EASE_IN = [0.7, 0, 0.84, 0] as const;

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const partClass: Record<NonNullable<TitlePart["style"]> | "plain", string> = {
  plain: "",
  italic: "font-black italic text-[#172b81] dark:text-[#a8c0ff]",
  select: "font-black italic text-[#172b81] dark:text-[#e1ae48]",
  marker: "font-black",
};

// GSAP SplitText-style reveal: every character sits inside a clipped word box,
// rises into place from below with a slight tilt, and on slide change lifts
// out the top the same way. Driven by the parent's hidden/visible/exit labels.
const SplitChars: React.FC<{
  as?: "h2" | "p";
  parts: TitlePart[];
  className?: string;
  stagger?: number;
  delay?: number;
}> = ({ as = "h2", parts, className, stagger = 0.03, delay = 0 }) => {
  const prefersReducedMotion = useReducedMotion();
  const Tag = as;
  let charIndex = -1;

  const charVariants = {
    hidden: prefersReducedMotion ? { opacity: 0 } : { y: "110%", rotate: 10 },
    visible: (i: number) => ({
      y: "0%",
      rotate: 0,
      opacity: 1,
      transition: { duration: prefersReducedMotion ? 0.3 : 0.9, ease: EASE_OUT, delay: delay + i * stagger },
    }),
    exit: (i: number) =>
      prefersReducedMotion
        ? { opacity: 0, transition: { duration: 0.2 } }
        : { y: "-110%", rotate: -6, transition: { duration: 0.45, ease: EASE_IN, delay: i * (stagger / 3) } },
  };

  const totalChars = parts.reduce((n, p) => n + p.text.replace(/\s/g, "").length, 0);
  // Highlights land once the letters have settled.
  const accentDelay = delay + totalChars * stagger + 0.35;
  const accentVariants = {
    hidden: { scaleX: 0 },
    visible: { scaleX: 1, transition: { duration: 0.5, delay: accentDelay, ease: [0.65, 0, 0.35, 1] as const } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  return (
    <Tag className={`relative isolate ${className ?? ""}`} aria-label={parts.map((p) => p.text).join(" ")}>
      {parts.map((part, pi) => {
        const words = part.text.split(" ").map((word, wi, all) => (
          <Fragment key={wi}>
            {/* Padding keeps descenders and italic overhangs from being clipped by the mask */}
            <span aria-hidden="true" className="inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em] pr-[0.05em] -mr-[0.05em]">
              {word.split("").map((char) => {
                charIndex += 1;
                return (
                  <motion.span key={charIndex} custom={charIndex} variants={charVariants} className="inline-block origin-bottom-left">
                    {char}
                  </motion.span>
                );
              })}
            </span>
            {wi < all.length - 1 ? " " : ""}
          </Fragment>
        ));

        return (
          <Fragment key={pi}>
            <span className={`relative inline-block ${partClass[part.style ?? "plain"]}`}>
              {part.style === "select" && (
                <motion.span
                  aria-hidden="true"
                  variants={accentVariants}
                  className="absolute -inset-x-[0.12em] inset-y-[0.04em] -z-10 origin-left border-2 border-[#e1ae48] bg-[#e1ae48]/25"
                >
                  {/* Caret handles, like a text selection in a design tool */}
                  <span className="absolute -left-[9px] -top-3 w-0 h-0 border-x-[7px] border-x-transparent border-t-[10px] border-t-[#e1ae48]" />
                  <span className="absolute -right-[9px] -bottom-3 w-0 h-0 border-x-[7px] border-x-transparent border-b-[10px] border-b-[#e1ae48]" />
                </motion.span>
              )}
              {part.style === "marker" && (
                <motion.span
                  aria-hidden="true"
                  variants={accentVariants}
                  className="absolute -inset-x-[0.1em] bottom-[0.12em] h-[0.42em] -z-10 origin-left -rotate-1 bg-[#e1ae48]/70 dark:bg-[#e1ae48]/45"
                />
              )}
              {words}
            </span>
            {pi < parts.length - 1 ? " " : ""}
          </Fragment>
        );
      })}
    </Tag>
  );
};

const stickerTone: Record<Sticker["tone"], string> = {
  gold: "rounded-full bg-[#e1ae48] text-black",
  navy: "rounded-full bg-[#172b81] text-white",
  dot: "rounded-full bg-white text-slate-900 border border-slate-300 dark:bg-[#1E1E1E] dark:text-white dark:border-white/15",
  bubble: "rounded-lg bg-[#e8eefc] text-[#172b81] border border-[#172b81]/15 dark:bg-[#1c2a66] dark:text-white dark:border-white/10",
};

// Tilted labels that pop onto the title after it lands, like stickers on a
// moodboard. They straighten up a little on hover.
const StickerTag: React.FC<{ sticker: Sticker; delay: number }> = ({ sticker, delay }) => {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.span
      aria-hidden="true"
      className={`absolute z-10 hidden md:inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold whitespace-nowrap shadow-[3px_4px_0_rgba(15,23,42,0.16)] cursor-default select-none ${stickerTone[sticker.tone]} ${sticker.className}`}
      variants={{
        hidden: prefersReducedMotion ? { opacity: 0, rotate: sticker.rotate } : { opacity: 0, scale: 0.4, rotate: 0 },
        visible: {
          opacity: 1,
          scale: 1,
          rotate: sticker.rotate,
          transition: prefersReducedMotion ? { duration: 0.3, delay } : { type: "spring", stiffness: 380, damping: 16, delay },
        },
        exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } },
      }}
      whileHover={prefersReducedMotion ? undefined : { rotate: 0, scale: 1.06 }}
    >
      {sticker.tone === "dot" && <span className="w-2.5 h-2.5 rounded-full bg-[#172b81] dark:bg-[#e1ae48]" />}
      {sticker.label}
      {sticker.tone === "bubble" && (
        <span className="absolute left-1/2 -bottom-[7px] -translate-x-1/2 w-3 h-3 rotate-45 bg-inherit border-r border-b border-inherit" />
      )}
      {sticker.cursor && (
        <MousePointer2 size={22} className="absolute -right-4 -bottom-5 fill-white text-slate-900 -rotate-12 drop-shadow-sm" />
      )}
    </motion.span>
  );
};

const ScrubWord: React.FC<{ word: string; progress: MotionValue<number>; start: number; end: number }> = ({
  word,
  progress,
  start,
  end,
}) => {
  const opacity = useTransform(progress, [start, end], [0.15, 1]);
  return <motion.span style={{ opacity }}>{word}</motion.span>;
};

// Scroll-scrubbed copy: words start faint and brighten one after another as
// the reader scrolls through the pinned slide, and dim again scrolling back.
const ScrubText: React.FC<{
  text: string;
  className?: string;
  progress: MotionValue<number>;
  from?: number;
  to?: number;
}> = ({ text, className, progress, from = 0.05, to = 0.75 }) => {
  const prefersReducedMotion = useReducedMotion();
  const words = text.split(" ");
  const span = (to - from) / words.length;

  if (prefersReducedMotion) return <p className={className}>{text}</p>;

  return (
    <p className={className} aria-label={text}>
      <span aria-hidden="true">
        {words.map((word, i, all) => (
          <Fragment key={i}>
            <ScrubWord word={word} progress={progress} start={from + i * span} end={from + (i + 1) * span} />
            {i < all.length - 1 ? " " : ""}
          </Fragment>
        ))}
      </span>
    </p>
  );
};

// Short blurbs can carry display-sized type; long paragraphs would overflow
// the pinned viewport, so they step down a size.
const bodyScale = (text: string) =>
  text.length < 130
    ? "text-xl sm:text-2xl md:text-3xl"
    : "text-base sm:text-lg md:text-xl";

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const idx = Math.min(slides.length - 1, Math.floor(latest * slides.length));
    setActiveIndex(Math.max(0, idx));
  });

  const goToSlide = (index: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const containerTop = window.scrollY + rect.top;
    // Each slide is pinned for exactly one viewport of scrolling.
    const targetScroll = containerTop + index * window.innerHeight + 4;
    window.scrollTo({ top: targetScroll, behavior: prefersReducedMotion ? "auto" : "smooth" });
  };

  const slide = slides[activeIndex];
  const titleChars = slide.title.reduce((n, p) => n + p.text.replace(/\s/g, "").length, 0);
  const stickerDelay = 0.15 + titleChars * 0.03 + 0.5;

  // 0 → 1 progress *within* the currently pinned slide, used to scrub the
  // body copy's row-by-row fill as the reader scrolls.
  const slideProgress = useTransform(scrollYProgress, (p) =>
    Math.min(1, Math.max(0, p * slides.length - activeIndex))
  );

  return (
    <div className="w-full [overflow-x:clip] bg-white dark:bg-[#121212]">
      {/* One extra viewport of height: the sticky child is 100vh tall, so the
          track needs n+1 screens for all n slides to get a full pinned turn
          (otherwise the last slide unpins the moment it appears). */}
      <div ref={containerRef} className="relative" style={{ height: `${(slides.length + 1) * 100}vh` }}>
        <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center bg-white dark:bg-[#121212]">

          {/* Paper: grid, grain and soft corner glows */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff0d_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0d_1px,transparent_1px)] bg-[size:36px_36px]" />
            <div className="absolute -top-40 -right-40 w-[420px] h-[420px] rounded-full bg-[#e1ae48]/45 dark:bg-[#e1ae48]/20 blur-[110px]" />
            <div className="absolute -bottom-40 -left-40 w-[380px] h-[380px] rounded-full bg-[#172b81]/20 dark:bg-[#172b81]/30 blur-[110px]" />
            <div className="absolute inset-0 opacity-[0.18] mix-blend-multiply dark:opacity-[0.06] dark:mix-blend-screen" style={{ backgroundImage: GRAIN }} />
          </div>

          {/* Slide content */}
          <div className="relative z-10 max-w-5xl px-6 md:px-10 text-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={{
                  hidden: { opacity: 1 },
                  visible: { opacity: 1 },
                  exit: { opacity: 0, transition: { duration: 0.3, delay: 0.2 } },
                }}
              >
                <SplitChars
                  as="p"
                  parts={[{ text: slide.eyebrow }]}
                  stagger={0.02}
                  className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-6 md:mb-8"
                />

                <div className="relative inline-block mb-8 md:mb-10">
                  <SplitChars
                    parts={slide.title}
                    delay={0.15}
                    className="text-5xl sm:text-7xl md:text-8xl font-medium tracking-tight text-slate-900 dark:text-white leading-[1.05]"
                  />
                  {slide.stickers.map((s, i) => (
                    <StickerTag key={s.label} sticker={s} delay={stickerDelay + i * 0.15} />
                  ))}
                </div>

                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.5, ease: EASE_OUT } },
                  }}
                >
                  {slide.body && (
                    <ScrubText
                      text={slide.body}
                      progress={slideProgress}
                      className={`${bodyScale(slide.body)} text-slate-700 dark:text-slate-200 font-light leading-relaxed max-w-3xl mx-auto`}
                    />
                  )}
                  {slide.bodyExtra && (
                    <ScrubText
                      text={slide.bodyExtra}
                      progress={slideProgress}
                      from={0.35}
                      to={0.9}
                      className={`mt-5 ${bodyScale(slide.bodyExtra)} text-slate-700 dark:text-slate-200 font-light leading-relaxed max-w-3xl mx-auto`}
                    />
                  )}
                </motion.div>

                {slide.features && (
                  <motion.div
                    className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 text-left"
                    initial="hidden"
                    animate="visible"
                    variants={{ visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } } }}
                  >
                    {slide.features.map((f) => (
                      <motion.div
                        key={f.title}
                        variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                        className="flex items-start gap-4 p-5 rounded-xl bg-white/80 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 shadow-[3px_4px_0_rgba(15,23,42,0.08)]"
                      >
                        <div className="text-[#172b81] dark:text-[#a8c0ff] mt-0.5 flex-shrink-0">{f.icon}</div>
                        <div>
                          <h4 className="font-bold text-slate-900 dark:text-white mb-1">{f.title}</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400 font-light">{f.text}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Slide navigation */}
          <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 z-20 hidden sm:flex flex-col items-center gap-4">
            {slides.map((s, i) => (
              <button
                key={s.eyebrow}
                onClick={() => goToSlide(i)}
                aria-label={`Go to ${s.title.map((p) => p.text).join(" ")}`}
                className="group relative flex items-center justify-center w-6 h-6"
              >
                <span
                  className={`rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? "w-2.5 h-2.5 bg-[#172b81] dark:bg-[#e1ae48]"
                      : "w-1.5 h-1.5 bg-slate-300 dark:bg-white/20 group-hover:bg-slate-400"
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Scroll hint */}
          {activeIndex < slides.length - 1 && (
            <motion.div
              className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-[10px] uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500"
              animate={prefersReducedMotion ? undefined : { y: [0, 6, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              Scroll
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default About;
